import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Table from "../../../../core/pagination/datatable";
import CommonFooter from "../../../../core/common/footer/commonFooter";
import { Spin, Tabs, Button, Select } from "antd";
import { BASE_CRM_URL } from "../../../components/config/config";
import dayjs from "dayjs";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import Swal from "sweetalert2";

const Notify = () => {
  const userDetails = JSON.parse(sessionStorage.getItem("userDetails") || "{}");

  const userId = userDetails?.staffid;

  const [loading, setLoading] = useState(true);
  const [markingRead, setMarkingRead] = useState(false);
  const [activeTab, setActiveTab] = useState("received");

  const [receivedData, setReceivedData] = useState([]);
  const [sentData, setSentData] = useState([]);

  const [receivedCount, setReceivedCount] = useState(0);
  const [sentCount, setSentCount] = useState(0);

  const [notificationFilter, setNotificationFilter] = useState("all");

  // const GET_NOTIFICATIONS = `${BASE_CRM_URL}notifications/GetNotifications?userid=${userId}`;

  const MARK_READ_API = `${BASE_CRM_URL}notifications/UpdateNotiAsRead`;

  const MARK_ALL_READ_API = `${BASE_CRM_URL}notifications/UpdateAllNotiAsRead`;
  // const DELETE_NOTIFICATION_API = `${BASE_CRM_URL}notifications/DeleteNoification`;

  // =========================================================
  // GET NOTIFICATIONS
  // =========================================================

  const getNotifications = async (filter = "all") => {
    try {
      setLoading(true);

      let url = `${BASE_CRM_URL}notifications/GetNotifications?userid=${userId}`;

      if (filter === "read") {
        url += `&isRead=1`;
      }

      if (filter === "unread") {
        url += `&isRead=0`;
      }

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error("Failed to fetch notifications");
      }

      const data = await response.json();

      if (data?.Status) {
        setReceivedData(data?.ReceivedNotifications || []);
        setSentData(data?.SentNotifications || []);

        setReceivedCount(data?.ReceivedCount || 0);
        setSentCount(data?.SentCount || 0);
      } else {
        setReceivedData([]);
        setSentData([]);
        setReceivedCount(0);
        setSentCount(0);
      }
    } catch (error) {
      console.error("Get Notifications Error:", error);
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // MARK SINGLE NOTIFICATION AS READ
  // =========================================================

  const markAsRead = async (notificationId) => {
    try {
      setMarkingRead(true);

      const response = await fetch(MARK_READ_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          notificationid: notificationId,
          userid: userId,
        }),
      });

      const data = await response.json();

      if (data?.Status) {
        setReceivedData((prev) =>
          prev.map((item) =>
            item.id === notificationId
              ? {
                  ...item,
                  isread: 1,
                  isread_inline: 1,
                }
              : item,
          ),
        );
        window.dispatchEvent(new Event("refresh-notifications"));
        await getNotifications();

        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Notification marked as read",
          timer: 1500,
          showConfirmButton: false,
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: data?.Message || "Failed to mark notification as read",
          timer: 2000,
          showConfirmButton: false,
        });
      }
    } catch (error) {
      console.error("Mark Read Error:", error);

      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to mark notification as read",
        timer: 2000,
        showConfirmButton: false,
      });
    } finally {
      setMarkingRead(false);
    }
  };

  // =========================================================
  // MARK ALL AS READ
  // =========================================================

  const markAllAsRead = async () => {
    try {
      setMarkingRead(true);

      const response = await fetch(MARK_ALL_READ_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          userid: userId,
        }),
      });

      const data = await response.json();

      if (data?.Status) {
        setReceivedData((prev) =>
          prev.map((item) => ({
            ...item,
            isread: 1,
            isread_inline: 1,
          })),
        );
        window.dispatchEvent(new Event("refresh-notifications"));
        await getNotifications();

        Swal.fire({
          icon: "success",
          title: "Success",
          text: "All notifications marked as read",
          timer: 1500,
          showConfirmButton: false,
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: data?.Message || "Failed to mark all notifications as read",
          timer: 2000,
          showConfirmButton: false,
        });
      }
    } catch (error) {
      console.error("Mark All Read Error:", error);

      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to mark all notifications as read",
        timer: 2000,
        showConfirmButton: false,
      });
    } finally {
      setMarkingRead(false);
    }
  };

  // =========================================================
  // SENT AGAIN
  // =========================================================

  const sendAgain = (record) => {
    console.log("Send notification again:", record);

    Swal.fire({
      icon: "info",
      title: "Send Notification Again",
      text: "Send-again functionality can be connected to your API here.",
      confirmButtonColor: "#1677ff",
    });
  };

  // =========================================================
  // INITIAL API
  // =========================================================

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    getNotifications();
  }, [userId]);

  // =========================================================
  // RECEIVED COLUMNS
  // =========================================================

  const columns = [
    {
      title: "Notification",
      dataIndex: "description",
      key: "description",

      sorter: (a, b) =>
        (a.description || "").localeCompare(b.description || ""),

      render: (description, record) => {
        const isUnread = Number(record?.isread) === 0;

        const fullText = description || "No description";

        const truncatedText =
          fullText.length > 100 ? `${fullText.substring(0, 100)}...` : fullText;

        return (
          <div
            className="d-flex align-items-start"
            style={{
              cursor: isUnread ? "pointer" : "default",
            }}
            onClick={() => {
              if (isUnread) {
                markAsRead(record.id);
              }
            }}
          >
            <div
              className={`rounded-circle d-flex align-items-center justify-content-center me-3 ${
                isUnread
                  ? "bg-primary text-white"
                  : "bg-soft-success text-success"
              }`}
              style={{
                width: "38px",
                height: "38px",
                minWidth: "38px",
              }}
            >
              <i className="fa-solid fa-bell"></i>
            </div>

            <div
              title={fullText}
              className={
                isUnread
                  ? "fw-bold text-dark pt-2"
                  : "fw-medium text-secondary pt-2"
              }
            >
              {truncatedText}
            </div>
          </div>
        );
      },
    },

    {
      title: "Date",
      dataIndex: "date",
      key: "date",

      sorter: (a, b) => dayjs(a.date).unix() - dayjs(b.date).unix(),

      render: (date) => (
        <OverlayTrigger
          placement="top"
          overlay={<Tooltip>Notification date</Tooltip>}
        >
          <div>
            <i className="fa-regular fa-calendar text-primary me-2"></i>

            {date ? dayjs(date).format("DD/MM/YYYY hh:mm A") : "N/A"}
          </div>
        </OverlayTrigger>
      ),
    },
    {
      title: "From",
      dataIndex: "FromStaffName",
      key: "FromStaffName",
      render: (value) => <span>{value || "-"}</span>,
    },

    {
      title: "Action",
      key: "action",

      align: "center",

      render: (_, record) => {
        const isUnread = Number(record?.isread) === 0;

        return (
          <div className="action-table-data">
            <div className="edit-delete-action">
              {isUnread ? (
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip>Mark notification as read</Tooltip>}
                >
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    disabled={markingRead}
                    onClick={() => markAsRead(record.id)}
                  >
                    <i className="fa-solid fa-check me-1"></i>
                    UnRead
                  </button>
                </OverlayTrigger>
              ) : (
                <span className="text-success">
                  <i className="fa-solid fa-check-double me-1"></i>
                  Read
                </span>
              )}
            </div>
          </div>
        );
      },
    },
  ];

  // =========================================================
  // SENT COLUMNS
  // =========================================================

  const sentColumns = [
    {
      title: "Notification",
      dataIndex: "description",
      key: "description",

      sorter: (a, b) =>
        (a.description || "").localeCompare(b.description || ""),

      render: (description) => {
        const fullText = description || "No description";

        const truncatedText =
          fullText.length > 50 ? `${fullText.substring(0, 50)}...` : fullText;

        return (
          <div className="d-flex align-items-start">
            <div
              className="rounded-circle d-flex align-items-center justify-content-center me-3 bg-soft-info text-info"
              style={{
                width: "38px",
                height: "38px",
                minWidth: "38px",
              }}
            >
              <i className="fa-solid fa-paper-plane"></i>
            </div>

            <div title={fullText} className="fw-medium text-dark pt-2">
              {truncatedText}
            </div>
          </div>
        );
      },
    },

    {
      title: "Date",
      dataIndex: "date",
      key: "date",

      sorter: (a, b) => dayjs(a.date).unix() - dayjs(b.date).unix(),

      render: (date) => (
        <OverlayTrigger
          placement="top"
          overlay={<Tooltip>Notification date</Tooltip>}
        >
          <div>
            <i className="fa-regular fa-calendar text-primary me-2"></i>

            {date ? dayjs(date).format("DD/MM/YYYY hh:mm A") : "N/A"}
          </div>
        </OverlayTrigger>
      ),
    },

    {
      title: "To",
      dataIndex: "ToStaffName",
      key: "ToStaffName",
      render: (value) => <span>{value || "-"}</span>,
    },

    // {
    //   title: "Action",
    //   key: "action",
    //   width: 160,
    //   align: "center",

    //   render: (_, record) => (
    //     <Button type="primary" size="small" onClick={() => sendAgain(record)}>
    //       <i className="fa-solid fa-paper-plane me-1"></i>
    //     </Button>
    //   ),
    // },
    {
      title: "Action",
      key: "action",

      align: "center",

      // No sorting for action
      render: (_, record) => (
        <div className="action-table-data">
          <div className="edit-delete-action">
            <Link
              title="Edit"
              onClick={() => sendAgain(record)}
              className="text-info me-2 p-2"
            >
              <i className="fa-solid fa-paper-plane text-info"></i>
            </Link>
            <Link
              title="Delete"
              onClick={() => deleteNotification(record.id)}
              className="text-danger me-2 p-2"
            >
              <i className="ti ti-trash text-danger"></i>
            </Link>
          </div>
        </div>
      ),
    },
  ];

  // =========================================================
  // TAB ITEMS - ONLY NAVIGATION
  // =========================================================

  const tabItems = [
    {
      key: "received",
      label: (
        <span>
          <i className="fa-solid fa-inbox me-2"></i>
          Received
          <span className="badge bg-primary ms-2">{receivedCount}</span>
        </span>
      ),
    },
    {
      key: "sent",
      label: (
        <span>
          <i className="fa-solid fa-paper-plane me-2"></i>
          Sent
          <span className="badge bg-secondary ms-2">{sentCount}</span>
        </span>
      ),
    },
  ];

  // =========================================================
  // CURRENT TABLE
  // =========================================================

  const currentData = activeTab === "received" ? receivedData : sentData;

  const currentColumns = activeTab === "received" ? columns : sentColumns;

  const hasUnreadNotifications = receivedData.some(
    (item) => Number(item?.isread) === 0,
  );

  // =========================================================
  // RETURN
  // =========================================================

  const deleteNotification = async (notificationId) => {
    try {
      const result = await Swal.fire({
        icon: "warning",
        title: "Delete Notification?",
        text: "Are you sure you want to delete this notification?",
        showCancelButton: true,
        confirmButtonText: "Yes, Delete",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#e40d22",
      });

      if (!result.isConfirmed) return;

      setLoading(true);

      const response = await fetch(
        `${BASE_CRM_URL}notifications/DeleteNoification`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            notificationid: notificationId,
          }),
        },
      );

      const data = await response.json();

      if (data?.Status) {
        // Immediately remove deleted record from UI
        setReceivedData((prev) =>
          prev.filter((item) => item.id !== notificationId),
        );

        setSentData((prev) =>
          prev.filter((item) => item.id !== notificationId),
        );

        // Refresh data from API
        await getNotifications(notificationFilter);

        Swal.fire({
          icon: "success",
          title: "Deleted!",
          text: data?.Message || "Notification deleted successfully.",
          timer: 1500,
          showConfirmButton: false,
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: data?.Message || "Failed to delete notification.",
        });
      }
    } catch (error) {
      console.error("Delete Notification Error:", error);

      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Something went wrong while deleting the notification.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* PAGE HEADER */}
        <div
          className="d-flex align-items-center justify-content-between mb-3 w-100 position-relative page-header"
          style={{ minHeight: "45px" }}
        >
          {/* LEFT */}
          <div className="d-flex align-items-center">
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to="/crm/dashboard">
                    <i className="fas fa-home fa-lg" />
                  </Link>
                </li>
                <h4 className="breadcrumb-item " aria-current="page">
                  Notifications
                </h4>
              </ol>
            </nav>
          </div>

          {/* CENTER */}
          <div
            className="position-absolute start-50 translate-middle-x"
            // style={{ top: "50%" }}
          >
            <Tabs
              activeKey={activeTab}
              items={tabItems}
              onChange={setActiveTab}
              centered
            />
          </div>

          {/* RIGHT */}
          <div className="ms-auto">
            {activeTab === "received" && (
              <Button
                type={hasUnreadNotifications ? "primary" : "default"}
                onClick={markAllAsRead}
                loading={markingRead}
                disabled={!hasUnreadNotifications}
              >
                <i className="fa-solid fa-check-double me-2"></i>
                Mark All as Read
              </Button>
            )}
          </div>
        </div>

        {/* TABLE */}
        <div className="card table-list-card">
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap ">
            <div className="search-set p-3">{/* search */}</div>

            <div className="d-flex table-dropdown my-xl-auto right-content align-items-center flex-wrap ">
              <div className="dropdown">
                {activeTab === "received" && (
                  <Select
                    value={notificationFilter}
                    style={{ width: 150, height: 38 }}
                    onChange={(value) => {
                      setNotificationFilter(value);
                      getNotifications(value);
                    }}
                    options={[
                      {
                        value: "all",
                        label: "All",
                      },
                      {
                        value: "read",
                        label: "Read",
                      },
                      {
                        value: "unread",
                        label: "Un Read",
                      },
                    ]}
                  />
                )}
              </div>
            </div>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              {loading ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "250px",
                  }}
                >
                  <Spin size="large" />
                </div>
              ) : (
                <Table
                  key={`${activeTab}-${currentData.length}-${notificationFilter}`}
                  columns={currentColumns}
                  dataSource={currentData}
                  rowKey="id"
                />
              )}
            </div>
          </div>
        </div>
      </div>
      <CommonFooter />
    </div>
  );
};

export default Notify;
